import React, {useCallback, useEffect, useRef, useState} from 'react';

import {List, ListItem} from '../list';
import {Button} from '../inputs/button';
import {NavigationIcon} from '../svg-icon';
import {SiteRoutes} from '../../../lib/routes';
import {cls} from '../../utils';
import {useLocalStorage} from '../../utils/hooks';
import {useOnClickOutside} from '../../use-on-click-outside';

import css from './nav-menu.module.css';

/** чуть дольше --duration-normal, чтобы переход успел закончиться */
const ANIMATION_TIME = 400;

export type NavMenuItem = {
	title: string;
	icon?: React.ReactNode;
	id: keyof SiteRoutes;
	routes: Array<keyof SiteRoutes>;
};

type Props = {
	items: NavMenuItem[];
	currentRoute: keyof SiteRoutes;
};

export const NavMenu: React.FC<Props> = ({items, currentRoute}) => {
	const selected = items.find((item) => item.routes.includes(currentRoute));
	const [expanded, setExpanded] = useLocalStorage('expandedMenu', true);
	const [open, setOpen] = useState(false);
	const [animated, setAnimated] = useState(false);

	const navRef = useRef<HTMLElement>(null);
	useOnClickOutside(navRef, () => {
		if (!open) return;
		setAnimated(true);
		setOpen(false);
	});

	useEffect(() => {
		if (!animated) return;
		const timer = setTimeout(() => setAnimated(false), ANIMATION_TIME);
		return () => clearTimeout(timer);
	}, [animated, open, expanded]);

	const handleToggle = useCallback(() => {
		setAnimated(true);
		const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 960px)').matches;
		if (isDesktop) {
			setExpanded(!expanded);
		} else {
			setOpen((prev) => !prev);
		}
	}, [expanded, setExpanded]);

	return (
		<>
			{open && <div className={css.overlay} aria-hidden />}
			<nav
				ref={navRef}
				className={cls(css.panel, open && css.open, expanded && css.expanded, animated && css.animated)}
				aria-label="Разделы сайта"
			>
				<Button
					appearance="subtle"
					icon={<NavigationIcon />}
					onClick={handleToggle}
					className={css.hamburger}
					tabIndex={0}
					aria-label="Меню"
				/>
				<List navigationMode="items" selectAppearance="line" className={css.menuList}>
					{items.map((item) => {
						const isSelected = item === selected;
						const icon = React.isValidElement(item.icon)
							? React.cloneElement(
									item.icon as React.ReactElement<{appearance?: string; size?: number}>,
									{
										appearance: isSelected ? 'filled' : undefined,
										size: 20,
									},
							  )
							: item.icon;

						return (
							<ListItem
								key={item.id}
								value={item.id}
								route={item.id}
								icon={icon}
								title={item.title}
								buttonClassName={css.item}
								selectedClassName={css.selectedItem}
								selected={isSelected}
							>
								<span className={css.label}>{item.title}</span>
							</ListItem>
						);
					})}
				</List>
			</nav>
		</>
	);
};
